import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

import { userActions } from '../../actions';
import PageTitle from '../../components/common/PageTitle';
import { userService } from '../../services';
import {
  getEffectiveUserSearch,
  getPlanView,
  getUserListQueryFromSearch,
  getUserListSearchFromQuery,
  USER_LIST_DEFAULT_QUERY,
} from '../../utils/usersList';

const SUPPORTED_SORT_FIELDS = ['username', 'createdAt', 'lastLogin'];

const planFormatter = plan => {
  if (!plan || plan.status === 'none') return <span className="text-muted">—</span>;

  const variant = {
    active: 'success',
    near: 'warning',
    expired: 'danger',
  }[plan.status];
  const label = {
    active: 'Active',
    near: 'Near Expiration',
    expired: 'Expired',
  }[plan.status];

  return (
    <div className="users-plan-cell">
      <Badge variant={variant}>{label}</Badge>
      <span className="users-plan-name">{plan.name}</span>
      {plan.days && <small className="text-muted">{plan.days}</small>}
    </div>
  );
};

const dateFormatter = value => (
  value ? String(value).split('T')[0] : <span className="text-muted">Never</span>
);

const dateTimeFormatter = value => {
  if (!value) return <span className="text-muted">Never</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <span className="text-muted">—</span>;

  return (
    <span title={date.toISOString()}>
      {date.toLocaleDateString('en-CA')}
    </span>
  );
};

const usernameFormatter = (username, row) => (
  <div className="tableTagBox">
    <span className="tableUsername">{username}</span>
    <span className="tableName">{row.fullName}</span>
  </div>
);

const roleFormatter = role => (
  <Badge variant={{ admin: 'danger', operator: 'primary', referral: 'info' }[role] || 'secondary'}>
    {role ? `${role.charAt(0).toUpperCase()}${role.slice(1)}` : 'Unknown'}
  </Badge>
);

const accountStatusFormatter = status => {
  const variant = {
    active: 'success',
    waiting: 'warning',
    block: 'danger',
  }[status] || 'secondary';

  const label = status === 'block'
    ? 'Blocked'
    : (status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : 'Unknown');
  return <Badge variant={variant}>{label}</Badge>;
};

const verifiedFormatter = verified => (
  <Badge variant={verified ? 'success' : 'secondary'}>
    {verified ? 'Verified' : 'Not verified'}
  </Badge>
);

const NewUserList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const loading = useSelector(state => state.user.loading);
  const error = useSelector(state => state.user.error);
  const users = useSelector(state => state.user.users);
  const total = useSelector(state => state.user.total);
  const [query, setQuery] = useState(() => (
    getUserListQueryFromSearch(location.search)
  ));
  const [searchInput, setSearchInput] = useState(query.search);
  const [quickUser, setQuickUser] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    const nextQuery = getUserListQueryFromSearch(location.search);
    setQuery(current => (
      JSON.stringify(current) === JSON.stringify(nextQuery) ? current : nextQuery
    ));
    setSearchInput(current => (current === nextQuery.search ? current : nextQuery.search));
  }, [location.search]);

  useEffect(() => {
    const nextSearch = getUserListSearchFromQuery(query);
    if (nextSearch !== location.search) {
      history.replace({ pathname: location.pathname, search: nextSearch });
    }
  }, [history, location.pathname, location.search, query]);

  useEffect(() => {
    dispatch(userActions.newDetails(query));
  }, [dispatch, query]);

  useEffect(() => {
    const effectiveSearch = getEffectiveUserSearch(searchInput);
    if (effectiveSearch === null) return undefined;

    const timeout = setTimeout(() => {
      setQuery(current => (
        current.search === effectiveSearch
          ? current
          : { ...current, page: 1, search: effectiveSearch }
      ));
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const usersList = useMemo(() => (
    Array.isArray(users)
      ? users.map(user => {
        const fullName = [user.firstname, user.lastname]
          .filter(Boolean)
          .join(' ');

        return {
          id: user._id,
          username: user.username,
          fullName: fullName || 'Not provided',
          role: user.role,
          status: user.status,
          verified: Boolean(user.verified),
          createdAt: user.createdAt,
          lastLogin: user.lastLogin,
          plan: getPlanView(user.planSummary),
          designsCount: Number(user.designsCount || 0),
        };
      })
      : []
  ), [users]);

  const openQuickView = useCallback(user => setQuickUser(user), []);
  const closeQuickView = useCallback(() => setQuickUser(null), []);
  const listUrl = `${location.pathname}${location.search}`;

  const actionFormatter = useCallback((userId, row) => (
    <div className="users-actions">
      <Button
        variant="outline-info"
        size="sm"
        onClick={() => openQuickView(row)}
        aria-label={`Quick view ${row.username}`}
        title="Quick view"
      >
        <i className="material-icons">visibility</i>
      </Button>
      <Button
        as={Link}
        variant="info"
        size="sm"
        to={{
          pathname: `/user/${userId}`,
          state: { from: listUrl },
        }}
      >
        Details
      </Button>
      <Button
        as="a"
        variant="outline-secondary"
        size="sm"
        href={`/user/${userId}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${row.username} in a new tab`}
        title="Open in new tab"
      >
        <i className="material-icons">open_in_new</i>
      </Button>
    </div>
  ), [listUrl, openQuickView]);

  const columns = useMemo(() => [
    {
      text: 'Username',
      dataField: 'username',
      formatter: usernameFormatter,
      sort: true,
    },
    {
      text: 'Role',
      dataField: 'role',
      formatter: roleFormatter,
    },
    {
      text: 'Status',
      dataField: 'status',
      formatter: accountStatusFormatter,
    },
    {
      text: 'Verification',
      dataField: 'verified',
      formatter: verifiedFormatter,
    },
    {
      text: 'Plan',
      dataField: 'plan',
      formatter: planFormatter,
    },
    {
      text: 'Designs',
      dataField: 'designsCount',
    },
    {
      text: 'Registered',
      dataField: 'createdAt',
      formatter: dateFormatter,
      sort: true,
    },
    {
      text: 'Last login',
      dataField: 'lastLogin',
      formatter: dateTimeFormatter,
      sort: true,
    },
    {
      text: 'Action',
      dataField: 'id',
      formatter: actionFormatter,
      csvExport: false,
    },
  ], [actionFormatter]);

  const handleTableChange = useCallback((type, state) => {
    if (type === 'pagination') {
      setQuery(current => ({
        ...current,
        page: state.page,
        limit: state.sizePerPage,
      }));
      return;
    }

    if (type === 'sort' && SUPPORTED_SORT_FIELDS.includes(state.sortField)) {
      setQuery(current => ({
        ...current,
        page: 1,
        sortBy: state.sortField,
        sortOrder: state.sortOrder === 'asc' ? 'asc' : 'desc',
      }));
    }
  }, []);

  const setFilter = (key, value) => {
    setQuery(current => {
      const next = { ...current, page: 1, [key]: value };
      if (key === 'planStatus' && value) next.role = 'client';
      if (key === 'role' && value && value !== 'client') next.planStatus = '';
      return next;
    });
  };

  const clearFilters = () => {
    setSearchInput('');
    setQuery(current => ({
      ...USER_LIST_DEFAULT_QUERY,
      limit: current.limit,
    }));
  };

  const exportFilteredUsers = async () => {
    setExporting(true);
    setExportError('');
    try {
      const response = await userService.exportDetails(query);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vibino-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setExportError(
        requestError?.response?.data?.message
          || requestError.message
          || 'Could not export users.',
      );
    } finally {
      setExporting(false);
    }
  };

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      Showing {from} to {to} of {size} Results
    </span>
  );

  const paginationOptions = {
    page: query.page,
    sizePerPage: query.limit,
    totalSize: total,
    hidePageListOnlyOnePage: true,
    showTotal: true,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: '10', value: 10 },
      { text: '25', value: 25 },
      { text: '50', value: 50 },
    ],
  };

  const searchLength = searchInput.trim().length;
  const searchTooShort = searchLength > 0 && searchLength < 3;
  const hasFilters = Boolean(
    query.search
      || query.planStatus
      || query.accountStatus
      || query.role
      || query.verified,
  );

  return (
    <Container fluid className="main-content-container px-4 users-list-page">
      <Row noGutters className="page-header py-4 align-items-center">
        <PageTitle
          sm="4"
          title="Users List"
          subtitle="Users"
          className="text-sm-left"
        />
        <Col className="text-right">
          <Button
            variant="outline-primary"
            disabled={exporting || loading}
            onClick={exportFilteredUsers}
          >
            {exporting ? (
              <><Spinner animation="border" size="sm" /> Exporting...</>
            ) : (
              <><i className="material-icons mr-1">download</i>Export filtered users</>
            )}
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger">
          <span>{error}</span>
          <Button
            variant="outline-danger"
            size="sm"
            className="ml-3"
            onClick={() => dispatch(userActions.newDetails(query))}
          >
            Retry
          </Button>
        </Alert>
      )}
      {exportError && <Alert variant="danger">{exportError}</Alert>}

      <div className="users-toolbar">
        <div className="users-toolbar-row">
          <Form.Group className="users-toolbar-search">
            <Form.Label>Search</Form.Label>
            <div className="users-search-box">
              <Form.Control
                aria-label="Search users"
                type="search"
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                placeholder="Username, name, mobile or email"
              />
              {searchInput && (
                <Button
                  variant="link"
                  className="users-search-clear"
                  onClick={() => setSearchInput('')}
                  aria-label="Clear user search"
                >
                  <i className="material-icons">close</i>
                </Button>
              )}
            </div>
            <Form.Text className={searchTooShort ? 'text-warning' : 'text-muted'}>
              {searchTooShort
                ? `${3 - searchLength} more character${searchLength === 2 ? '' : 's'} required`
                : 'Type at least 3 characters to search.'}
            </Form.Text>
          </Form.Group>
          <Form.Group className="users-toolbar-field">
            <Form.Label htmlFor="users-role-filter">Role</Form.Label>
            <Form.Control
              id="users-role-filter"
              as="select"
              value={query.role}
              onChange={event => setFilter('role', event.target.value)}
              disabled={loading}
            >
              <option value="">All roles</option>
              <option value="admin">Admin</option>
              <option value="client">Client</option>
              <option value="operator">Operator</option>
              <option value="referral">Referral</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="users-toolbar-field">
            <Form.Label htmlFor="users-account-status-filter">Account status</Form.Label>
            <Form.Control
              id="users-account-status-filter"
              as="select"
              value={query.accountStatus}
              onChange={event => setFilter('accountStatus', event.target.value)}
              disabled={loading}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="waiting">Waiting</option>
              <option value="block">Blocked</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="users-toolbar-field">
            <Form.Label htmlFor="users-verification-filter">Verification</Form.Label>
            <Form.Control
              id="users-verification-filter"
              as="select"
              value={query.verified}
              onChange={event => setFilter('verified', event.target.value)}
              disabled={loading}
            >
              <option value="">All users</option>
              <option value="true">Verified</option>
              <option value="false">Not verified</option>
            </Form.Control>
          </Form.Group>

          <Form.Group className="users-toolbar-plan">
            <Form.Label>Plan status</Form.Label>
            <ButtonGroup>
              {[
                ['', 'All'],
                ['active', 'Active'],
                ['near', 'Near expiration'],
                ['expired', 'Expired'],
              ].map(([value, label]) => (
                <Button
                  key={value || 'all'}
                  disabled={loading}
                  variant={query.planStatus === value ? 'primary' : 'outline-secondary'}
                  onClick={() => setFilter('planStatus', value)}
                >
                  {label}
                </Button>
              ))}
            </ButtonGroup>
          </Form.Group>

          <Form.Group className="users-toolbar-clear">
            <Form.Label aria-hidden="true">Actions</Form.Label>
            <Button
              variant="outline-secondary"
              className="w-100"
              disabled={!hasFilters || loading}
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Form.Group>

          <div className="users-toolbar-count">
            <Form.Label aria-hidden="true">Results</Form.Label>
            <span className="users-result-count" aria-live="polite">
              {loading && <Spinner animation="border" size="sm" className="mr-2" />}
              {loading ? 'Updating users...' : `${total} users`}
            </span>
          </div>
        </div>
      </div>

      <div className="users-table-wrapper">
        <BootstrapTable
          key={`${query.sortBy}-${query.sortOrder}`}
          keyField="id"
          data={usersList}
          columns={columns}
          remote={{ pagination: true, sort: true, filter: false, cellEdit: false }}
          loading={loading}
          defaultSorted={[{ dataField: query.sortBy, order: query.sortOrder }]}
          pagination={paginationFactory(paginationOptions)}
          onTableChange={handleTableChange}
          noDataIndication={() => (
            loading ? 'Loading users...' : 'No users match the selected filters.'
          )}
          bordered={false}
          hover
          wrapperClasses="table-responsive"
        />
      </div>

      <Modal show={Boolean(quickUser)} onHide={closeQuickView} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Quick user view</Modal.Title>
        </Modal.Header>
        {quickUser && (
          <Modal.Body className="users-quick-view">
            <div className="users-quick-heading">
              <div>
                <h5>{quickUser.username}</h5>
                <span className="text-muted">{quickUser.fullName}</span>
              </div>
              <div className="users-quick-badges">
                {roleFormatter(quickUser.role)}
                {accountStatusFormatter(quickUser.status)}
                {verifiedFormatter(quickUser.verified)}
              </div>
            </div>
            <Row>
              <Col md="3"><strong>Plan</strong>{planFormatter(quickUser.plan)}</Col>
              <Col md="3"><strong>Designs</strong><span>{quickUser.designsCount}</span></Col>
              <Col md="3"><strong>Registered</strong><span>{dateFormatter(quickUser.createdAt)}</span></Col>
              <Col md="3"><strong>Last login</strong><span>{dateTimeFormatter(quickUser.lastLogin)}</span></Col>
            </Row>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeQuickView}>Close</Button>
          {quickUser && (
            <>
              <Button
                as={Link}
                variant="primary"
                to={{
                  pathname: `/user/${quickUser.id}`,
                  state: { from: listUrl },
                }}
                onClick={closeQuickView}
              >
                Open full profile
              </Button>
              <Button
                as="a"
                variant="outline-primary"
                href={`/user/${quickUser.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in new tab
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default NewUserList;
